/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { z } from "zod";
import {
  Box,
  Button,
  Input,
  Label,
  SelectButton,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  MappingRuleType,
  useCreateMappingRuleMutation,
  MappingRulesDocument,
} from "~/generated/graphql";
import { showError, showSuccess } from "~/services/toast";
import { StyledMultiSelect } from "~/views/events/components/DateRangeSelector/styles";
import { MAX_TAG_KEY_LENGTH } from "../../constants";

type Props = {
  onClose: () => void;
};

const typeOptions = [
  {
    value: MappingRuleType.Application,
    label: "Application",
  },
  {
    value: MappingRuleType.Environment,
    label: "Environment",
  },
];

const tagNameSchema = z.object({
  tagName: z.string().max(MAX_TAG_KEY_LENGTH, {
    message: `Tag Key cannot exceed ${MAX_TAG_KEY_LENGTH} characters.`,
  }),
});

export function AddMappingRuleForm({ onClose }: Props) {
  const [tagKey, setTagKey] = useState("");
  const [tagKeyError, setTagKeyError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<MappingRuleType>(
    MappingRuleType.Application
  );

  const handleTagKeyChange = (value: string) => {
    setTagKey(value);
    // Validate using zod schema
    const result = tagNameSchema.safeParse({ tagName: value });
    if (result.success) {
      setTagKeyError(null);
    } else {
      // Display the first validation error message
      setTagKeyError(result.error.errors[0].message);
    }
  };

  const [createMappingRule, { loading }] = useCreateMappingRuleMutation({
    refetchQueries: [{ query: MappingRulesDocument }],
    onCompleted: (data) => {
      if (data.createMappingRule) {
        showSuccess("Mapping Rule created successfully!");
        onClose();
      } else {
        showError("Failed to create Mapping Rule.");
      }
    },
    onError: () => {
      showError("An error occurred while creating the Mapping Rule.");
    },
  });

  const handleSubmit = async () => {
    const result = tagNameSchema.safeParse({ tagName: tagKey });
    if (!result.success) {
      setTagKeyError(result.error.errors[0].message);
      return;
    }

    await createMappingRule({
      variables: {
        input: {
          tagName: tagKey,
          type: selectedType,
        },
      },
    });
  };

  return (
    <Box spacing="spacing04">
      <Label name="Choose Application or Environment" htmlFor="tagKey" />
      <StyledMultiSelect size="small">
        {typeOptions.map((type) => {
          return (
            <SelectButton
              key={type.value}
              name={type.value}
              value={type.value}
              labelText={type.label}
              isActive={selectedType === type.value}
              onClick={() => setSelectedType(type.value)}
            />
          );
        })}
      </StyledMultiSelect>
      <Box spacing="spacing01" direction="row" />
      <Label name="Tag Key" htmlFor="tagKey" required />
      <Input
        id="tagKey"
        value={tagKey}
        onChange={(e) => handleTagKeyChange(e.target.value)}
        placeholder="Enter Tag Key"
        status={tagKeyError ? "danger" : undefined}
      />
      {tagKeyError && (
        <Text size="sm" color={theme.color.text.warning}>
          {tagKeyError}
        </Text>
      )}
      <Box spacing="spacing01" direction="row" />
      <Button
        onClick={handleSubmit}
        disabled={!tagKey || loading || tagKeyError !== null}
      >
        {loading ? "Adding..." : "Add"}
      </Button>
    </Box>
  );
}
